import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Zap, QrCode, ArrowRight, Settings, Smartphone, Loader2, CheckCircle2, Mail, Building2, User, Phone, MapPinned, Crown, Sparkles, Users, Shield, TrendingUp, Palette, Building, Handshake, Mic, Headphones, CreditCard, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'pro', label: 'Pro / Agency' },
];

// Plan pricing (matches backend PLAN_PRICING)
const PLAN_PRICING = {
  starter: { monthly: 999, yearly: 9990, limit: 50 },
  growth: { monthly: 1999, yearly: 19990, limit: 100 },
};

// Base pricing and volume discounts for display
const PRICING = {
  starter: { base: 599, discounted: 399, limit: 50 },
  growth: { base: 999, discounted: 799, limit: 100 },
  pro: { base: 1599, discounted: 1299, limit: 250 },
};

export const LandingPage = () => {
  const [waitlistForm, setWaitlistForm] = useState({ 
    name: '',
    business_name: '', 
    email: '',
    phone: '',
    city: '',
    plan: '',
    location_count: 1,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Pricing controls
  const [locationCount, setLocationCount] = useState([1]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  // Checkout modal state
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    city: '',
  });
  const [processing, setProcessing] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [purchaseComplete, setPurchaseComplete] = useState(false);

  // Load Razorpay script and payment config
  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    // Fetch payment config
    fetch(`${API}/payment/config`)
      .then(res => res.json())
      .then(data => setPaymentConfig(data))
      .catch(err => console.error('Failed to load payment config:', err));

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Calculate price based on location count
  const getPrice = (plan) => {
    const pricing = PRICING[plan];
    if (locationCount[0] >= 4) {
      return { current: pricing.discounted, original: pricing.base, hasDiscount: true };
    }
    return { current: pricing.base, original: pricing.base, hasDiscount: false };
  };

  // Get actual purchase price for a plan
  const getPurchasePrice = (plan) => {
    if (!PLAN_PRICING[plan]) return null;
    const pricing = PLAN_PRICING[plan];
    return billingCycle === 'yearly' ? pricing.yearly : pricing.monthly;
  };

  // Get yearly savings
  const getYearlySavings = (plan) => {
    if (!PLAN_PRICING[plan]) return 0;
    const pricing = PLAN_PRICING[plan];
    return (pricing.monthly * 12) - pricing.yearly;
  };

  const scrollToWaitlist = () => {
    const element = document.getElementById('waitlist-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Open checkout modal for a specific plan
  const openCheckout = (plan) => {
    if (!paymentConfig?.razorpay_enabled) {
      toast.error('Payments are currently unavailable. Please join the waitlist.');
      scrollToWaitlist();
      return;
    }
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  // Handle plan purchase
  const handlePurchase = async (e) => {
    e.preventDefault();
    
    if (!checkoutForm.name || !checkoutForm.email || !checkoutForm.phone || !checkoutForm.business_name || !checkoutForm.city) {
      toast.error('Please fill in all fields');
      return;
    }

    setProcessing(true);
    
    try {
      // Create order
      const orderRes = await fetch(`${API}/payment/create-plan-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...checkoutForm,
          plan: selectedPlan,
          billing_cycle: billingCycle,
        }),
      });

      if (!orderRes.ok) {
        const err = await orderRes.json();
        throw new Error(err.detail || 'Failed to create order');
      }

      const orderData = await orderRes.json();

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: 'Socialxia Review',
        description: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan - ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API}/payment/verify-plan`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            if (!verifyRes.ok) {
              throw new Error('Payment verification failed');
            }

            const result = await verifyRes.json();
            setPurchaseComplete(true);
            toast.success('Payment successful! Check your email for login credentials.');
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: checkoutForm.name,
          email: checkoutForm.email,
          contact: checkoutForm.phone,
        },
        theme: {
          color: '#10b981',
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (err) {
      toast.error(err.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    
    if (!waitlistForm.name || !waitlistForm.business_name || !waitlistForm.email || !waitlistForm.phone || !waitlistForm.city || !waitlistForm.plan) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...waitlistForm,
        location_count: locationCount[0],
        billing_preference: 'monthly'
      };
      
      const res = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (!res.ok) throw new Error('Failed to submit');

      const result = await res.json();
      setSubmitted(true);
      toast.success(result.message || 'Thanks! We will contact you shortly.');
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const starterPrice = getPrice('starter');
  const growthPrice = getPrice('growth');
  const proPrice = getPrice('pro');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-glow">
                  <Star className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-secondary flex items-center justify-center">
                  <MapPin className="w-2.5 h-2.5 text-secondary-foreground" />
                </div>
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold text-foreground">Socialxia Review</h1>
                <p className="text-xs text-muted-foreground">Google Maps Prefill</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Powered by GPT-4o</span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={scrollToWaitlist}
                data-testid="nav-request-access-btn"
              >
                Request Access
              </Button>
              <Link to="/login">
                <Button variant="outline" size="sm" data-testid="login-btn">
                  Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden gradient-hero py-20 lg:py-28">
        <div className="absolute inset-0 pattern-dots opacity-30" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1 text-center lg:text-left">
              <Badge variant="teal" className="mb-6 inline-flex">
                <QrCode className="w-3.5 h-3.5 mr-1.5" />
                QR to Google Maps Review
              </Badge>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Instant Google Maps
                <span className="block text-primary">Review Prefill</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                Customers scan a QR, tap their service, and get redirected to Google Maps 
                with an AI-generated review ready to submit. No typing, no copying.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button variant="premium" size="lg" onClick={scrollToWaitlist}>
                  <Sparkles className="w-5 h-5" />
                  Get Started
                </Button>
                <Link to="/login">
                  <Button variant="outline" size="lg">
                    <Settings className="w-5 h-5" />
                    Open Dashboard
                  </Button>
                </Link>
              </div>
            </div>

            {/* Flow Preview */}
            <div className="flex-1 w-full max-w-lg">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-2xl" />
                
                <div className="relative bg-card rounded-2xl border shadow-xl p-6 space-y-6">
                  <div className="text-center">
                    <Badge variant="coral" className="mb-3">How It Works</Badge>
                    <h3 className="font-heading text-lg font-semibold text-foreground">3-Step Flow</h3>
                  </div>

                  <div className="space-y-4">
                    {[
                      { step: 1, icon: QrCode, text: "Customer scans QR code" },
                      { step: 2, icon: Smartphone, text: "Selects their service" },
                      { step: 3, icon: MapPin, text: "Redirected to Google Maps" }
                    ].map((item) => (
                      <div key={item.step} className="flex items-center gap-4 p-3 rounded-xl bg-muted/50">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <item.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <span className="text-sm text-muted-foreground">Step {item.step}</span>
                          <p className="font-medium text-foreground">{item.text}</p>
                        </div>
                        {item.step < 3 && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground text-center">
                      Review auto-generated by AI &bull; No manual typing
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
              Why Socialxia Review?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Perfect for Restaurants, Salons, Real Estate Agents, and any local business looking to boost their Google reviews.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Zero Friction",
                description: "No copy-paste, no typing. Direct redirect to Google Maps with review prefilled.",
                icon: Zap
              },
              {
                title: "AI-Generated",
                description: "GPT-4o creates natural, human-sounding reviews based on your business services.",
                icon: Star
              },
              {
                title: "QR Ready",
                description: "Generate QR codes for your business. Place them at reception for easy access.",
                icon: QrCode
              }
            ].map((feature, i) => (
              <Card key={i} variant="glass" className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge variant="teal" className="mb-4">
              <Crown className="w-3.5 h-3.5 mr-1.5" />
              Simple Pricing
            </Badge>
            <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
              Choose Your Plan
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Stop chasing reviews. Start collecting them automatically.
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center p-1 rounded-full bg-muted border border-border">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly' 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly' 
                    ? 'bg-primary text-primary-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Yearly
                <Badge variant="success" className="text-xs">Save 2 months</Badge>
              </button>
            </div>
          </div>

          {/* 3 Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <Card variant="glass" className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="text-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
                    <Zap className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground mb-1">Starter</h3>
                  <div className="mb-1">
                    {billingCycle === 'yearly' && (
                      <span className="text-sm text-muted-foreground line-through mr-2">
                        ₹{PLAN_PRICING.starter.monthly * 12}
                      </span>
                    )}
                    <span className="text-2xl font-bold text-foreground">
                      ₹{getPurchasePrice('starter')}
                    </span>
                    <span className="text-sm font-normal text-muted-foreground">
                      /{billingCycle === 'yearly' ? 'year' : 'mo'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-xs text-success font-medium">Save ₹{getYearlySavings('starter')}/year</p>
                  )}
                  <Badge variant="outline" className="mt-2">
                    {PLAN_PRICING.starter.limit} AI Reviews / mo
                  </Badge>
                </div>
                <ul className="space-y-2.5 mb-5 text-sm">
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
                    <span>Standard QR Code</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
                    <span>Direct Google Link</span>
                  </li>
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  size="sm" 
                  onClick={() => openCheckout('starter')}
                  data-testid="buy-starter-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Buy Now
                </Button>
              </CardContent>
            </Card>

            {/* Growth Plan - Most Popular */}
            <Card variant="elevated" className="relative hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-primary/50 ring-2 ring-primary/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge variant="premium" className="shadow-lg">
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  Most Popular
                </Badge>
              </div>
              <CardContent className="pt-8">
                <div className="text-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground mb-1">Growth</h3>
                  <div className="mb-1">
                    {billingCycle === 'yearly' && (
                      <span className="text-sm text-muted-foreground line-through mr-2">
                        ₹{PLAN_PRICING.growth.monthly * 12}
                      </span>
                    )}
                    <span className="text-2xl font-bold text-primary">
                      ₹{getPurchasePrice('growth')}
                    </span>
                    <span className="text-sm font-normal text-muted-foreground">
                      /{billingCycle === 'yearly' ? 'year' : 'mo'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-xs text-success font-medium">Save ₹{getYearlySavings('growth')}/year</p>
                  )}
                  <Badge variant="coral" className="mt-2">
                    {PLAN_PRICING.growth.limit} AI Reviews / mo
                  </Badge>
                </div>
                <ul className="space-y-2.5 mb-5 text-sm">
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <Shield className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span><strong className="text-foreground">Negative Feedback Shield</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <Palette className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>Custom QR Codes</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <Mic className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>Tone of Voice</span>
                  </li>
                </ul>
                <Button 
                  variant="premium" 
                  className="w-full" 
                  size="sm" 
                  onClick={() => openCheckout('growth')}
                  data-testid="buy-growth-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Buy Now
                </Button>
              </CardContent>
            </Card>

            {/* Pro / Agency Plan - Contact Sales */}
            <Card variant="glass" className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="pt-6">
                <div className="text-center mb-5">
                  <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mx-auto mb-3">
                    <Building className="w-6 h-6 text-secondary-foreground" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground mb-1">Enterprise</h3>
                  <div className="mb-1">
                    <span className="text-2xl font-bold text-foreground">
                      Custom
                    </span>
                  </div>
                  <Badge variant="warning" className="mt-2">
                    Unlimited AI Reviews
                  </Badge>
                </div>
                <ul className="space-y-2.5 mb-5 text-sm">
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <TrendingUp className="w-4 h-4 text-secondary-foreground shrink-0 mt-0.5" />
                    <span><strong className="text-foreground">SEO Keyword Ranking</strong></span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <Headphones className="w-4 h-4 text-secondary-foreground shrink-0 mt-0.5" />
                    <span>Priority Support</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <Building className="w-4 h-4 text-secondary-foreground shrink-0 mt-0.5" />
                    <span>Multi-Location Dashboard</span>
                  </li>
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full" 
                  size="sm" 
                  onClick={scrollToWaitlist}
                  data-testid="contact-sales-btn"
                >
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist-section" className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card variant="elevated" className="overflow-hidden">
            <CardContent className="p-8 sm:p-12">
              {submitted ? (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-success" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-foreground mb-2">
                    You're on the list!
                  </h3>
                  <p className="text-muted-foreground">
                    Thanks for your interest! We'll contact you shortly to set up your account.
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <Badge variant="coral" className="mb-4">
                      <Star className="w-3.5 h-3.5 mr-1.5" />
                      Limited Access
                    </Badge>
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">
                      Request Access
                    </h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Join our waitlist and we'll reach out to set up your business on Socialxia Review.
                    </p>
                  </div>

                  <form onSubmit={handleWaitlistSubmit} className="space-y-4 max-w-md mx-auto">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        Full Name
                      </label>
                      <Input
                        placeholder="John Doe"
                        value={waitlistForm.name}
                        onChange={(e) => setWaitlistForm(p => ({ ...p, name: e.target.value }))}
                        data-testid="waitlist-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        Business Name
                      </label>
                      <Input
                        placeholder="e.g., Sunrise Restaurant"
                        value={waitlistForm.business_name}
                        onChange={(e) => setWaitlistForm(p => ({ ...p, business_name: e.target.value }))}
                        data-testid="waitlist-business-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          placeholder="+91 98765 43210"
                          value={waitlistForm.phone}
                          onChange={(e) => setWaitlistForm(p => ({ ...p, phone: e.target.value }))}
                          data-testid="waitlist-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground flex items-center gap-2">
                          <MapPinned className="w-4 h-4 text-muted-foreground" />
                          City
                        </label>
                        <Input
                          placeholder="Mumbai"
                          value={waitlistForm.city}
                          onChange={(e) => setWaitlistForm(p => ({ ...p, city: e.target.value }))}
                          data-testid="waitlist-city"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        Email Address
                      </label>
                      <Input
                        type="email"
                        placeholder="you@business.com"
                        value={waitlistForm.email}
                        onChange={(e) => setWaitlistForm(p => ({ ...p, email: e.target.value }))}
                        data-testid="waitlist-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-2">
                        <Crown className="w-4 h-4 text-muted-foreground" />
                        Interested Plan
                      </label>
                      <Select 
                        value={waitlistForm.plan} 
                        onValueChange={(value) => setWaitlistForm(p => ({ ...p, plan: value }))}
                      >
                        <SelectTrigger data-testid="waitlist-plan">
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {PLANS.map((plan) => (
                            <SelectItem key={plan.value} value={plan.value}>
                              {plan.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Location info */}
                    <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
                      <p><strong>Locations:</strong> {locationCount[0]}</p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      variant="premium" 
                      size="lg" 
                      className="w-full"
                      disabled={submitting}
                      data-testid="waitlist-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="w-5 h-5" />
                          Join Waitlist
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      We'll never share your information with third parties.
                    </p>
                  </form>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            Socialxia Review &copy; 2025
          </p>
        </div>
      </footer>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={(open) => {
        if (!processing) {
          setShowCheckout(open);
          if (!open) {
            setPurchaseComplete(false);
            setCheckoutForm({ name: '', email: '', phone: '', business_name: '', city: '' });
          }
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              {purchaseComplete ? 'Purchase Complete!' : `Get ${selectedPlan?.charAt(0).toUpperCase()}${selectedPlan?.slice(1)} Plan`}
            </DialogTitle>
            <DialogDescription>
              {purchaseComplete 
                ? 'Your account has been created successfully!' 
                : `${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} subscription - ₹${selectedPlan ? getPurchasePrice(selectedPlan) : 0}`
              }
            </DialogDescription>
          </DialogHeader>

          {purchaseComplete ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-success" />
              </div>
              <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                Welcome to Socialxia Review!
              </h3>
              <p className="text-muted-foreground mb-4">
                Check your email for login credentials. You can now access your dashboard and start collecting reviews!
              </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => setShowCheckout(false)}>
                  Close
                </Button>
                <Link to="/login">
                  <Button variant="premium">
                    Go to Login
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handlePurchase} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  Full Name
                </label>
                <Input
                  placeholder="John Doe"
                  value={checkoutForm.name}
                  onChange={(e) => setCheckoutForm(p => ({ ...p, name: e.target.value }))}
                  disabled={processing}
                  data-testid="checkout-name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="you@business.com"
                  value={checkoutForm.email}
                  onChange={(e) => setCheckoutForm(p => ({ ...p, email: e.target.value }))}
                  disabled={processing}
                  data-testid="checkout-email"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  Phone Number
                </label>
                <Input
                  placeholder="+91 98765 43210"
                  value={checkoutForm.phone}
                  onChange={(e) => setCheckoutForm(p => ({ ...p, phone: e.target.value }))}
                  disabled={processing}
                  data-testid="checkout-phone"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  Business Name
                </label>
                <Input
                  placeholder="Your Business Name"
                  value={checkoutForm.business_name}
                  onChange={(e) => setCheckoutForm(p => ({ ...p, business_name: e.target.value }))}
                  disabled={processing}
                  data-testid="checkout-business"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-2">
                  <MapPinned className="w-4 h-4 text-muted-foreground" />
                  City
                </label>
                <Input
                  placeholder="Mumbai"
                  value={checkoutForm.city}
                  onChange={(e) => setCheckoutForm(p => ({ ...p, city: e.target.value }))}
                  disabled={processing}
                  data-testid="checkout-city"
                />
              </div>

              {/* Order Summary */}
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">Plan</span>
                  <span className="font-medium text-foreground">
                    {selectedPlan?.charAt(0).toUpperCase()}{selectedPlan?.slice(1)} ({billingCycle})
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="text-lg font-bold text-primary">
                    ₹{selectedPlan ? getPurchasePrice(selectedPlan) : 0}
                  </span>
                </div>
                {billingCycle === 'yearly' && selectedPlan && (
                  <p className="text-xs text-success mt-2 text-right">
                    You save ₹{getYearlySavings(selectedPlan)} with yearly billing!
                  </p>
                )}
              </div>

              <Button 
                type="submit" 
                variant="premium" 
                size="lg" 
                className="w-full"
                disabled={processing}
                data-testid="checkout-pay-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Pay ₹{selectedPlan ? getPurchasePrice(selectedPlan) : 0}
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Secure payment powered by Razorpay
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
